import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, CheckCircle2, MinusCircle, Loader2, TrendingUp,
} from 'lucide-react';
import { brandingRootStyle, brandingProductName } from '../lib/embedBranding';
import { useEmbedToken } from '../lib/embedTokenBridge';
import { emit, emitDrill, subscribe } from '../lib/embedChannel';
import EmbedDrillBreadcrumb from '../components/embed/EmbedDrillBreadcrumb';

/**
 * Embed KPI Monitor (iframe surface).
 *
 * Mounted at `/embed/v2/kpi-monitor?token=<jwt>`.
 * Mirrors EmbedInsightsPage's contract: token from query, authenticate,
 * fetch data, render, emit postMessage events. Zero INZARA branding.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

async function authenticate(token) {
  const res = await fetch(`${API}/api/embed/v2/authenticate`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json())?.detail?.message || `authenticate ${res.status}`);
  return res.json();
}

async function fetchKpis(token) {
  const res = await fetch(`${API}/api/embed/v2/kpi-monitor`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error((await res.json())?.detail?.message || `kpi ${res.status}`);
  return res.json();
}

const STATUS_MAP = {
  ok:      { cls: 'border-emerald-500/40 bg-emerald-500/5', icon: CheckCircle2, tone: 'text-emerald-400', label: 'On track' },
  warn:    { cls: 'border-amber-500/40 bg-amber-500/5',     icon: AlertTriangle, tone: 'text-amber-400',   label: 'Watch' },
  alert:   { cls: 'border-rose-500/40 bg-rose-500/5',       icon: AlertTriangle, tone: 'text-rose-400',    label: 'Alert' },
  breach:  { cls: 'border-rose-500/40 bg-rose-500/5',       icon: AlertTriangle, tone: 'text-rose-400',    label: 'Breach' },
  unknown: { cls: 'border-border/40 bg-background/40',       icon: MinusCircle,   tone: 'text-muted-foreground', label: 'No data' },
};

function KpiCard({ kpi, statusHighlight }) {
  const s = STATUS_MAP[kpi.status] || STATUS_MAP.unknown;
  const Icon = s.icon;
  const pct = (kpi.target && kpi.last_value != null)
    ? Math.min(100, Math.max(0, (Number(kpi.last_value) / Number(kpi.target)) * 100))
    : null;
  const dimmed = statusHighlight && statusHighlight !== kpi.status;
  return (
    <button
      onClick={() => emitDrill('kpi', {
        kpi_id: kpi.kpi_id,
        name: kpi.name,
        status: kpi.status,
        last_value: kpi.last_value,
        unit: kpi.unit,
      })}
      className={`text-left rounded-lg border ${s.cls} p-4 hover:border-primary/40 transition-all ${dimmed ? 'opacity-30' : ''}`}
      data-testid={`kpi-card-${kpi.kpi_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{kpi.name}</div>
          <div className="text-2xl font-semibold mt-1 tabular-nums">
            {kpi.last_value != null ? `${kpi.last_value}${kpi.unit ? ' ' + kpi.unit : ''}` : '—'}
          </div>
        </div>
        <Icon className={`w-4 h-4 shrink-0 ${s.tone}`} />
      </div>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>Target: {kpi.target != null ? `${kpi.target}${kpi.unit ? ' ' + kpi.unit : ''}` : '—'}</span>
        <span className={s.tone}>{s.label}</span>
      </div>
      {pct != null && (
        <div className="mt-2 h-1 rounded-full bg-muted/30 overflow-hidden">
          <div className={`h-full ${kpi.status === 'alert' || kpi.status === 'breach' ? 'bg-rose-400' : kpi.status === 'warn' ? 'bg-amber-400' : 'bg-emerald-400'}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </button>
  );
}

export default function EmbedKpiMonitorPage() {
  const token = useEmbedToken();
  const [session, setSession] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusHighlight, setStatusHighlight] = useState(null);
  const [drillCrumb, setDrillCrumb] = useState(null);

  useEffect(() => {
    if (!token) { setError('Missing ?token= in URL'); setLoading(false); return; }
    let alive = true;
    (async () => {
      try {
        const s = await authenticate(token);
        if (!alive) return;
        setSession(s);
        emit('viewer.identified', { workspace_id: s.workspace_id, sub: s.sub, roles: s.roles });
        const feed = await fetchKpis(token);
        if (alive) {
          setKpis(feed.data || []);
          emit('kpi.loaded', { count: feed.data?.length ?? 0 });
        }
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
        emit('error', { message: String(e?.message || e) });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [token]);

  // Host → embed command channel.
  useEffect(() => {
    return subscribe({
      'filter.set': ({ key, value }) => {
        if (key === 'status' && ['ok', 'warn', 'alert', 'breach'].includes(value)) {
          setStatusHighlight((prev) => {
            if (prev === value) return prev;
            emit('filter.applied', { key: 'status', value, previous: prev });
            return value;
          });
        }
      },
      'filter.clear': ({ key }) => {
        if (!key || key === 'status') {
          setStatusHighlight((prev) => {
            if (prev == null) return prev;
            emit('filter.cleared', { key: 'status' });
            return null;
          });
        }
      },
      'ping': () => {
        if (session) {
          emit('viewer.identified', { workspace_id: session.workspace_id, sub: session.sub, roles: session.roles });
        }
      },
      'drill.response': ({ target, label, ttl_ms }) => {
        if (typeof label !== 'string' || !label.trim()) return;
        setDrillCrumb({ target, label: label.trim(), ttl_ms });
      },
    });
  }, [session]);

  const dismissDrill = useCallback((reason) => {
    setDrillCrumb((prev) => {
      if (prev) emit('drill.dismissed', { target: prev.target, reason });
      return null;
    });
  }, []);

  const summary = useMemo(() => {
    const s = { ok: 0, warn: 0, alert: 0, breach: 0, unknown: 0 };
    kpis.forEach(k => { s[k.status] = (s[k.status] || 0) + 1; });
    return s;
  }, [kpis]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="embed-kpi-error">
        <div className="max-w-md rounded-lg border border-rose-500/40 bg-rose-500/5 p-5 text-center">
          <AlertTriangle className="w-6 h-6 text-rose-400 mx-auto mb-2" />
          <div className="text-sm font-semibold mb-1">Couldn&apos;t load KPI monitor</div>
          <div className="text-xs text-muted-foreground break-words">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-background text-foreground"
      data-testid="embed-kpi-page"
      style={brandingRootStyle(session?.branding)}
    >
      <div className="max-w-4xl mx-auto p-5">
        {drillCrumb && (
          <EmbedDrillBreadcrumb
            crumb={drillCrumb}
            onDismiss={dismissDrill}
            primaryColor={session?.branding?.primary_color}
          />
        )}
        {session && (
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/25">
            <div className="flex items-center gap-3 min-w-0">
              {session.branding?.logo_url && (
                <img
                  src={session.branding.logo_url}
                  alt=""
                  className="h-6 w-auto max-w-[120px] object-contain shrink-0"
                  data-testid="embed-kpi-logo"
                />
              )}
              <div className="min-w-0">
                <div
                  className="text-[10px] uppercase tracking-widest text-muted-foreground truncate"
                  data-testid="embed-kpi-product-name"
                >
                  {brandingProductName(session.branding, 'KPI Monitor')}
                </div>
                <div className="text-sm truncate">
                  <span className="font-medium">{session.email || session.sub}</span>
                  <span className="text-muted-foreground ml-2 text-xs">· {(session.roles || []).join(', ') || 'viewer'}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <StatusChip icon={CheckCircle2}   tone="text-emerald-400" count={summary.ok}    label="OK" />
              <StatusChip icon={AlertTriangle}  tone="text-amber-400"   count={summary.warn}  label="Watch" />
              <StatusChip icon={AlertTriangle}  tone="text-rose-400"    count={summary.alert + summary.breach} label="Alert" />
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-8" data-testid="embed-kpi-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : kpis.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/40 py-10 px-6 text-center" data-testid="embed-kpi-empty">
            <Activity className="w-5 h-5 mx-auto text-muted-foreground/80 mb-2" />
            <div className="text-sm font-medium">No KPI definitions in this workspace yet</div>
            <div className="text-[11px] text-muted-foreground mt-1">Once KPIs are configured, they will show up here in real time.</div>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="embed-kpi-grid"
          >
            {kpis.map(k => <KpiCard key={k.kpi_id} kpi={k} statusHighlight={statusHighlight} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusChip({ icon: Icon, tone, count, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className={`w-3 h-3 ${tone}`} />
      <span className={tone}>{count}</span>
      <span>{label}</span>
    </span>
  );
}
